import React, {useEffect, useRef, useState} from 'react';
import {Pressable, ScrollView, Text, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {CAROUSEL_INTERVAL_MS, LOGIN_CAROUSEL} from '../loginHero';

interface Props {
  /** Width the carousel gets to fill (page width minus screen padding). */
  width: number;
  /** Inactive-dot color — override on dark backgrounds where the default
   * (near-black, low-opacity) dot is invisible. */
  dotInactiveColor?: string;
}

// Fixed so every slide's card is the same height regardless of how many
// lines its title/sub wrap to at the narrowest width this is used at —
// sized for the worst case (2-line title + 2-line sub) so nothing clips.
const CARD_HEIGHT = 268;

// A clone of slide 0 appended after the real last slide. Scrolling forward
// onto this clone looks identical to reaching slide 0 — once the scroll
// animation lands on it, we jump (unanimated) back to the real slide 0, so
// the loop reads as continuous instead of visibly rewinding across every
// card to get from the last slide back to the first.
const SLIDES = [...LOGIN_CAROUSEL, LOGIN_CAROUSEL[0]];

const LOOP_RESET_DELAY_MS = 450;

/**
 * One-card feature showcase for the login hero. Plain paging ScrollView —
 * no carousel dependency: auto-advances, loops, and pauses while the user
 * drags. Dots mirror the active page.
 */
export default function FeatureCarousel({
  width,
  dotInactiveColor = CCColors.Login.Border,
}: Props) {
  const scrollRef = useRef<ScrollView>(null);
  const [page, setPage] = useState(0);
  const pageRef = useRef(0);
  const interacting = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (interacting.current) {
        return;
      }
      const current = pageRef.current;
      const wrapping = current === LOGIN_CAROUSEL.length - 1;
      const targetIndex = wrapping ? LOGIN_CAROUSEL.length : current + 1;
      scrollRef.current?.scrollTo({x: targetIndex * width, animated: true});
      if (wrapping) {
        setTimeout(() => {
          if (!interacting.current) {
            scrollRef.current?.scrollTo({x: 0, animated: false});
            pageRef.current = 0;
            setPage(0);
          }
        }, LOOP_RESET_DELAY_MS);
      }
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [width]);

  const onScroll = (e: any) => {
    const rounded =
      Math.round(e.nativeEvent.contentOffset.x / width) % LOGIN_CAROUSEL.length;
    pageRef.current = rounded;
    setPage(rounded);
  };

  const endInteracting = () => {
    interacting.current = false;
  };

  const goToPage = (i: number) => {
    pageRef.current = i;
    setPage(i);
    scrollRef.current?.scrollTo({x: i * width, animated: true});
  };

  return (
    <View style={{width}}>
      <ScrollView
        ref={scrollRef}
        style={{width}}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={onScroll}
        onScrollBeginDrag={() => (interacting.current = true)}
        onScrollEndDrag={endInteracting}
        onMomentumScrollEnd={endInteracting}>
        {SLIDES.map((slide, i) => (
          <View key={`${slide.key}-${i}`} style={{width, paddingHorizontal: 8}}>
            <View
              style={{
                height: CARD_HEIGHT,
                backgroundColor: CCColors.Login.CardBg,
                borderRadius: 22,
                borderWidth: 1,
                borderColor: CCColors.Login.Border,
                paddingVertical: 26,
                paddingHorizontal: 22,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <View
                style={{
                  width: 62,
                  height: 62,
                  borderRadius: 100,
                  backgroundColor: CCColors.LightYellow,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                {/* Raw Text: system font renders bare chess codepoints in
                    text color (no emoji fallback). */}
                <Text
                  allowFontScaling={false}
                  style={{fontSize: 30, color: CCColors.Login.Ink}}>
                  {slide.glyph}
                </Text>
              </View>
              <CCText
                fontSize={19}
                fontWeight="semibold"
                color={CCColors.Login.Ink}
                style={{marginTop: 16, textAlign: 'center', lineHeight: 23}}
                numberOfLines={2}>
                {slide.title}
              </CCText>
              <CCText
                fontSize={14.5}
                color={CCColors.Login.Label}
                style={{
                  marginTop: 6,
                  textAlign: 'center',
                  lineHeight: 20,
                  paddingHorizontal: 8,
                }}
                numberOfLines={2}>
                {slide.sub}
              </CCText>
            </View>
          </View>
        ))}
      </ScrollView>
      <View
        style={{flexDirection: 'row', justifyContent: 'center', marginTop: 10}}>
        {LOGIN_CAROUSEL.map((slide, i) => (
          <Pressable
            key={slide.key}
            onPress={() => goToPage(i)}
            hitSlop={8}
            style={{paddingVertical: 8, paddingHorizontal: 3}}>
            <View
              style={{
                width: i === page ? 16 : 6,
                height: 6,
                borderRadius: 100,
                backgroundColor: i === page ? CCColors.Login.Gold : dotInactiveColor,
              }}
            />
          </Pressable>
        ))}
      </View>
    </View>
  );
}
